import { useEffect, useRef, useState } from "react";
import { Check, Copy, Download, RefreshCw, RotateCcw } from "lucide-react";
import type {
  MindsetCheckoutOperation,
  MindsetCheckoutSession,
  MindsetCustomerIntegrationHandoff,
  MindsetIntegrationProvisioningOperation,
} from "@mindset/api-client";
import { IntegrationEntitlementGate, type IntegrationEntitlementSnapshot } from "./IntegrationEntitlementGate";
import type { IntegrationCheckoutClient, IntegrationProvisioningClient } from "./integrationSetupClient";

type JourneyClient = Pick<IntegrationCheckoutClient, "currentCheckout" | "refreshCheckout"> &
  Pick<IntegrationProvisioningClient, "currentProvisioning" | "requestProvisioning" | "customerHandoff">;

interface Props {
  companyId: string;
  userId: string;
  applicationId: string;
  environment: "sandbox" | "production";
  client: JourneyClient;
  entitlement?: IntegrationEntitlementSnapshot;
  onChanged?: () => void;
  refreshVersion?: number;
  activationManaged?: boolean;
}

type JourneyStatus =
  | "checking"
  | "payment-pending"
  | "payment-failed"
  | "provisioning-in-progress"
  | "provisioning-failed"
  | "handoff-ready"
  | "not-ready";

const provisioningLabels: Record<MindsetIntegrationProvisioningOperation["state"], string> = {
  PENDING: "Provisioning queued",
  PROCESSING: "Provisioning in progress",
  AWAITING_ACK: "Waiting for runtime acknowledgment",
  COMPLETED: "Provisioning complete",
  FAILED: "Provisioning failed",
  CANCELED: "Provisioning canceled",
};

const checkoutLabels: Record<MindsetCheckoutSession["status"], string> = {
  pending: "Payment pending",
  open: "Payment pending",
  processing: "Payment processing",
  complete: "Payment verified",
  expired: "Payment failed or expired",
  reconciliation_required: "Payment recovery needed",
};

export function IntegrationCustomerJourney(props: Props) {
  return <CustomerJourney key={`${props.companyId}:${props.userId}:${props.applicationId}:${props.environment}`} {...props} />;
}

function CustomerJourney({ companyId, userId, applicationId, environment, client, entitlement, onChanged, refreshVersion, activationManaged }: Props) {
  const active = useRef(false);
  const [busy, setBusy] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [copyStatus, setCopyStatus] = useState("");
  const [error, setError] = useState("");
  const [checkout, setCheckout] = useState<MindsetCheckoutOperation | null>(null);
  const [session, setSession] = useState<MindsetCheckoutSession | null>(null);
  const [provisioning, setProvisioning] = useState<MindsetIntegrationProvisioningOperation | null>(null);
  const [handoff, setHandoff] = useState<MindsetCustomerIntegrationHandoff | null>(null);
  const [version, setVersion] = useState(0);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  useEffect(() => {
    let current = true;
    let stage: "status" | "payment" | "handoff" = "status";
    setBusy(true); setError(""); setCopyStatus(""); setHandoff(null);
    setCheckout(null); setSession(null); setProvisioning(null);
    void (async () => {
      const [currentCheckout, currentProvisioning] = await Promise.all([
        client.currentCheckout(companyId, userId, environment),
        client.currentProvisioning?.(companyId, userId, applicationId) ?? Promise.resolve(null),
      ]);
      if (!current || !active.current) return;
      if (currentCheckout && (currentCheckout.environment !== environment || currentCheckout.companyId !== companyId)) throw new Error("Checkout scope mismatch");
      if (currentProvisioning && (currentProvisioning.companyId !== companyId || currentProvisioning.applicationId !== applicationId)) {
        throw new Error("Provisioning scope mismatch");
      }
      setCheckout(currentCheckout);
      setProvisioning(currentProvisioning);
      let paymentVerified = !currentCheckout;
      if (currentCheckout) {
        stage = "payment";
        const observed = await client.refreshCheckout(companyId, userId, currentCheckout.id);
        if (!current || !active.current) return;
        setSession(observed);
        paymentVerified = observed.status === "complete";
      } else setSession(null);
      if (paymentVerified && currentProvisioning?.state === "COMPLETED" && client.customerHandoff) {
        stage = "handoff";
        const value = await client.customerHandoff(companyId, userId, applicationId);
        if (!current || !active.current) return;
        if (value.customerConfiguration.applicationId !== applicationId ||
            value.customerConfiguration.environment !== environment ||
            value.productionAcceptance !== false ||
            value.controls.noProviderCall !== true ||
            value.controls.noSecretRead !== true) throw new Error("Handoff scope mismatch");
        setHandoff(value);
      }
    })().catch(() => {
      if (current && active.current) {
        setHandoff(null);
        setError(stage === "handoff"
          ? "SDK handoff could not be loaded. Verify application access and refresh status."
          : stage === "payment"
            ? "Payment status could not be verified. Refresh status before continuing."
            : "Customer onboarding status could not be loaded. Retry status before continuing.");
      }
    }).finally(() => { if (current && active.current) setBusy(false); });
    return () => { current = false; };
  }, [client, companyId, userId, applicationId, environment, version, refreshVersion]);

  const status = resolveStatus({ busy, session, provisioning, handoff, error });
  const entitlementAllowsHandoff = entitlement?.sdkHandoffAllowed ?? true;
  async function retryProvisioning() {
    if (retrying || !["FAILED", "CANCELED"].includes(provisioning?.state ?? "")) return;
    setRetrying(true); setError(""); setCopyStatus("");
    try {
      const operation = await client.requestProvisioning(companyId, userId, applicationId, crypto.randomUUID());
      if (!active.current) return;
      if (operation.companyId !== companyId || operation.applicationId !== applicationId) throw new Error("Provisioning scope mismatch");
      setProvisioning(operation);
      setHandoff(null);
      onChanged?.();
    } catch {
      if (active.current) setError("Provisioning retry could not be started. Check payment and application ownership, then retry.");
    } finally { if (active.current) setRetrying(false); }
  }
  async function copyHandoff() {
    if (!handoff) return;
    try {
      await navigator.clipboard.writeText(handoff.markdown);
      if (active.current) setCopyStatus("Handoff copied.");
    } catch {
      if (active.current) setCopyStatus("Handoff could not be copied. Download it instead.");
    }
  }
  function downloadHandoff() {
    if (!handoff) return;
    const url = URL.createObjectURL(new Blob([handoff.markdown], { type: "text/markdown;charset=utf-8" }));
    const link = document.createElement("a");
    try {
      link.href = url; link.download = `mindset-${handoff.customerConfiguration.environment}-customer-handoff.md`;
      link.hidden = true; document.body.appendChild(link); link.click();
    } finally {
      link.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  }

  return <section className="integration-customer-journey" aria-labelledby={`journey-${applicationId}`}>
    <header>
      <div>
        <h3 id={`journey-${applicationId}`}>Customer onboarding</h3>
        <p>Individuals are free. New company subscriptions are USD 36/year flat plus USD 12/year per business-owned @handle. Existing subscriptions are unchanged.</p>
      </div>
      <button type="button" className="portal-icon-button" title="Refresh customer onboarding" aria-label="Refresh customer onboarding"
        disabled={busy || retrying} onClick={() => setVersion(value => value + 1)}><RefreshCw size={17} aria-hidden="true" /></button>
    </header>
    <ol className="integration-journey-steps" aria-label="Customer onboarding progress">
      <JourneyStep title="Payment" state={session?.status === "complete" && !busy ? "complete" : paymentState(status)} detail={session ? checkoutLabels[session.status] : checkout ? "Payment pending" : "No checkout in progress"} />
      <JourneyStep title="Provisioning" state={provisioning?.state === "COMPLETED" && !busy ? "complete" : provisioningState(status)}
        detail={provisioning ? provisioningLabels[provisioning.state] : session?.status === "complete" ? "Waiting for verified subscription reconciliation" : "Provisioning waits for verified payment"} />
      <JourneyStep title="SDK and config handoff" state={handoff && entitlementAllowsHandoff ? "complete" : "waiting"}
        detail={handoff && entitlementAllowsHandoff ? "Handoff available" : entitlementAllowsHandoff ? "Available after verified payment and completed provisioning" : "Blocked until subscription recovery completes"} />
      <JourneyStep title="Local smoke test" state={handoff && entitlementAllowsHandoff ? "ready" : "waiting"}
        detail={handoff && entitlementAllowsHandoff ? "Run the referenced local smoke validation before external acceptance" : "Smoke steps unlock with the handoff"} />
    </ol>
    {entitlement ? <IntegrationEntitlementGate entitlement={entitlement} /> : null}
    {status === "payment-pending" ? <p role="status">Payment pending. Return from Stripe and refresh status after payment completes.</p> : null}
    {status === "payment-failed" ? <p role="alert">Payment failed or needs recovery. Use checkout recovery or choose another plan before provisioning can continue.</p> : null}
    {status === "provisioning-in-progress" && provisioning?.state !== "COMPLETED" ? <p role="status">Provisioning in progress. Runtime acknowledgment and customer configuration are still being prepared.</p> : null}
    {status === "provisioning-failed" ? <div className="integration-recovery-panel" role="alert">
      <p>Provisioning failed or was canceled. Retry after confirming payment, application ownership, domain proof, and callback access.</p>
      {!activationManaged ? <button type="button" className="portal-button" disabled={retrying} onClick={() => void retryProvisioning()}><RotateCcw size={16} aria-hidden="true" />{retrying ? "Starting retry..." : "Retry provisioning"}</button> : null}
    </div> : null}
    {handoff && entitlementAllowsHandoff ? <HandoffPanel handoff={handoff} copyStatus={copyStatus} onCopy={() => void copyHandoff()} onDownload={downloadHandoff} /> : null}
    {error ? <p role="alert">{error}</p> : null}
  </section>;
}

function JourneyStep({ title, state, detail }: { title: string; state: "waiting" | "ready" | "active" | "complete" | "failed"; detail: string }) {
  return <li data-state={state}>
    <span className="integration-step-icon">{state === "complete" ? <Check size={15} aria-hidden="true" /> : null}</span>
    <div><strong>{title}</strong><p>{detail}</p></div>
  </li>;
}

function HandoffPanel({ handoff, copyStatus, onCopy, onDownload }: {
  handoff: MindsetCustomerIntegrationHandoff;
  copyStatus: string;
  onCopy: () => void;
  onDownload: () => void;
}) {
  const config = handoff.customerConfiguration;
  return <div className="integration-handoff-panel">
    <h4>SDK/config handoff available</h4>
    <dl className="integration-config-summary">
      <div><dt>Environment</dt><dd>{config.environment}</dd></div>
      <div><dt>Tenant</dt><dd>{config.tenantId}</dd></div>
      <div><dt>Application</dt><dd>{config.applicationId}</dd></div>
      <div><dt>Gateway</dt><dd>{config.gatewayUrl}</dd></div>
      <div><dt>Realtime</dt><dd>{config.realtimeUrl}</dd></div>
      <div><dt>Release</dt><dd>{handoff.releaseVersion}</dd></div>
    </dl>
    <div className="integration-handoff-actions">
      <button type="button" className="portal-button portal-button--primary" onClick={onCopy}><Copy size={16} aria-hidden="true" />Copy handoff</button>
      <button type="button" className="portal-button" onClick={onDownload}><Download size={16} aria-hidden="true" />Download handoff</button>
    </div>
    {copyStatus ? <p role="status">{copyStatus}</p> : null}
    <h5>Callback checklist</h5>
    <ul className="integration-checklist">
      {Object.entries(handoff.callbackContracts).flatMap(([name, contract]) =>
        contract.controls.map(control => <li key={`${name}:${control}`}><Check size={14} aria-hidden="true" />{control}</li>))}
    </ul>
    <h5>Local smoke next steps</h5>
    <ul className="integration-smoke-steps">
      {handoff.localSmokeValidation.map(item => <li key={item.id}><code>{item.command}</code></li>)}
    </ul>
    <p className="integration-availability">Production acceptance remains false. Live billing, storage, deployment, domain, store and device proof remain protected blockers.</p>
  </div>;
}

function resolveStatus(input: {
  busy: boolean;
  session: MindsetCheckoutSession | null;
  provisioning: MindsetIntegrationProvisioningOperation | null;
  handoff: MindsetCustomerIntegrationHandoff | null;
  error: string;
}): JourneyStatus {
  if (input.busy) return "checking";
  if (input.handoff) return "handoff-ready";
  if (input.session?.status === "expired" || input.session?.status === "reconciliation_required") return "payment-failed";
  if (input.session && input.session.status !== "complete") return "payment-pending";
  if (input.error) return "not-ready";
  if (input.provisioning) {
    if (["FAILED", "CANCELED"].includes(input.provisioning.state)) return "provisioning-failed";
    if (["PENDING", "PROCESSING", "AWAITING_ACK", "COMPLETED"].includes(input.provisioning.state)) return "provisioning-in-progress";
  }
  return "not-ready";
}

function paymentState(status: JourneyStatus): "waiting" | "ready" | "active" | "complete" | "failed" {
  if (status === "payment-failed") return "failed";
  if (status === "payment-pending") return "active";
  if (["provisioning-in-progress", "provisioning-failed", "handoff-ready"].includes(status)) return "complete";
  return "waiting";
}

function provisioningState(status: JourneyStatus): "waiting" | "ready" | "active" | "complete" | "failed" {
  if (status === "provisioning-failed") return "failed";
  if (status === "provisioning-in-progress") return "active";
  if (status === "handoff-ready") return "complete";
  return "waiting";
}
